/**
 * アクセシビリティアナウンサーユーティリティ
 *
 * スクリーンリーダー向けのアナウンスを効率的に管理します。
 * DOM要素の再利用により、パフォーマンスを向上させます。
 */

let announcerElement: HTMLElement | null = null;
let announcementTimer: NodeJS.Timeout | null = null;

/**
 * アナウンサー要素を取得または作成
 */
function getAnnouncerElement(): HTMLElement {
  if (!announcerElement || !document.body.contains(announcerElement)) {
    announcerElement = document.createElement('div');
    announcerElement.setAttribute('role', 'status');
    announcerElement.setAttribute('aria-live', 'polite');
    announcerElement.setAttribute('aria-atomic', 'true');
    announcerElement.style.position = 'absolute';
    announcerElement.style.left = '-10000px';
    announcerElement.style.width = '1px';
    announcerElement.style.height = '1px';
    announcerElement.style.overflow = 'hidden';
    document.body.appendChild(announcerElement);
  }
  return announcerElement;
}

/**
 * スクリーンリーダーにメッセージをアナウンス
 *
 * @param message - アナウンスするメッセージ
 * @param priority - 'polite' または 'assertive'
 * @param duration - メッセージを表示する時間（ミリ秒）
 */
export function announce(
  message: string,
  priority: 'polite' | 'assertive' = 'polite',
  duration = 3000
): void {
  // 既存のタイマーをクリア
  if (announcementTimer) {
    clearTimeout(announcementTimer);
    announcementTimer = null;
  }

  const element = getAnnouncerElement();

  // 優先度を更新
  element.setAttribute('aria-live', priority);

  // メッセージを設定（一旦クリアしてから設定することで確実に読み上げられる）
  element.textContent = '';

  // 次のフレームでメッセージを設定
  requestAnimationFrame(() => {
    element.textContent = message;
  });

  // 一定時間後にメッセージをクリア
  announcementTimer = setTimeout(() => {
    if (element.textContent === message) {
      element.textContent = '';
    }
  }, duration);
}
